import React from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';

import Tooltip from '@components/common/tooltip/main';
import { BFLoader } from '@components/common/loader/main';
import {
  bulkRefreshSections,
  Brandfolder,
  pluralizeObject
} from '@helpers/show_page_helpers';
import renderModal from '../modals/renderModal';


class BulkMoveModal extends React.Component {
  state = {
    brandfolderOptions: [],
    defaultAssetType: null,
    destinationBrandfolderKey: Brandfolder.key,
    destinationSectionKey: null,
    disableSubmitButton: true,
    errorState: false,
    isLoading: true,
    sameBrandfolder: true,
    sectionOptions: []
  }

  componentDidMount() {
    this.setDefaultAssetType();
  }

  componentWillUnmount() {
    this.props.closeModal();
  }

  setDefaultAssetType() {
    // this.props.selectedAssetTypes is a Set
    if (this.props.selectedAssetTypes.size > 1) {
      this.setState({ errorState: 'multipleTypes', isLoading: false });
    } else {
      const defaultAssetType = this.props.selectedAssetTypes.values().next().value;

      this.setState({ defaultAssetType }, () => {
        this.getTransferrableBrandfolders();
        this.getTransferrableSections();
      });
    }
  }

  getTransferrableBrandfolders() {
    const defaultAssetType = this.state.defaultAssetType;

    $.ajax({
      type: "POST",
      url: `/api/v4/bulk_actions/assets/transferrable_brandfolders?queue_priority=high`,
      contentType: "application/json",
      data: JSON.stringify({
        organization_key: BF.fx.organization().key,
        default_asset_type: defaultAssetType,
        brandfolder_key: Brandfolder.key,
        per: 1000
      }),
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: (response) => {
        const sortedBrandfolders = response.data.sort((a, b) => {
          const compA = a.attributes.name.toLowerCase();
          const compB = b.attributes.name.toLowerCase();
          return compA.localeCompare(compB);
        });
        this.setState({ brandfolderOptions: sortedBrandfolders });
      },
      error: () => {
        this.setState({ errorState: true });
      },
      complete: () => {
        this.setState({ isLoading: false });
      }
    });
  }

  getTransferrableSections(destinationBrandfolderKey = null) {
    if (this.state.defaultAssetType) {
      $.ajax({
        type: "POST",
        url: `/api/v4/bulk_actions/assets/transferrable_sections?queue_priority=high`,
        contentType: "application/json",
        context: this,
        data: JSON.stringify({
          data: { asset_keys: Array.from(this.props.selectedAssetKeys) },
          brandfolder_key: this.state.destinationBrandfolderKey || destinationBrandfolderKey,
          default_asset_type: this.state.defaultAssetType,
          per: 1000
        }),
        headers: { Authorization: `Bearer ${BF_Token}` },
        success: (response) => {
          if (response.data.length < 1) {
            this.setState({ errorState: 'noSectionOptions' });
          } else {
            this.setState({
              sectionOptions: response.data,
              errorState: false
            });
          }
        },
        error: () => {
          this.setState({ errorState: true });
        },
        complete: () => {
          this.setState({ isLoading: false });
        }
      });
    } else {
      this.setState({ errorState: 'missingDefaultAssetType' });
    }
  }

  selectOptionsFor(resourceType) {
    const resources = (resourceType.toLowerCase() === 'brandfolders')
      ? this.state.brandfolderOptions
      : this.state.sectionOptions;

    return resources.map((resource) => ({ value: resource.id, label: resource.attributes.name }));
  }

  updateSelectedOption(option, stateVariable) {
    const updatedValue = option == null ? null : option.value;

    if (stateVariable === 'destinationBrandfolderKey') {
      if (updatedValue !== null) {
        this.getTransferrableSections(updatedValue);
      }

      this.setState({
        destinationBrandfolderKey: updatedValue,
        destinationSectionKey: null,
        disableSubmitButton: true,
        errorState: false
      });
    } else if (stateVariable === 'destinationSectionKey') {
      this.setState({
        destinationSectionKey: updatedValue,
        disableSubmitButton: option == null,
      });
    }
  }


  buildDataBody() {
    return { data: {
      asset_keys: Array.from(this.props.selectedAssetKeys),
      bulk_move: {
        destination_section_key: this.state.destinationSectionKey
      } } };
  }

  moveAssets() {
    this.setState({ disableSubmitButton: true, isLoading: true });

    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);

    $.ajax({
      type: "PUT",
      url: '/api/v4/bulk_actions/assets/move?queue_priority=high',
      contentType: "application/json",
      data: JSON.stringify(this.buildDataBody()),
      context: this,
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: () => {
        bulkRefreshSections(this.props.selectedAssetKeys, null, [this.state.destinationSectionKey]);
        this.props.closeModal();
        const assetCopy = assetQtyObject.plural ? t('Assets') : t('Asset');
        Notify.create({
          title: `${assetQtyObject.qty} ${assetCopy} ${t('moved!')}`,
          type: 'success'
        });
        if (!this.state.sameBrandfolder) {
          BF.fx.updateAssetPageCount(assetQtyObject.qty, false);
        }
        this.props.addRemoveSelected([]);
      },
      error: () => {
        this.props.closeModal();
        const assetCopy = assetQtyObject.plural ? t('Assets') : t('Asset');
        Notify.create({
          title: `${t('An error occurred when moving')} ${assetQtyObject.qty} ${assetCopy}. ${t('Please try again.')}`,
          type: 'error'
        });
      }
    });
  }

  renderSectionSelection = () => {
    const { sameBrandfolder, destinationBrandfolderKey } = this.state;
    let disabledClass = '';

    // enable when Brandfolder has been selected
    if (!sameBrandfolder && destinationBrandfolderKey == null) {
      disabledClass = 'disabled-section';
    }

    if (this.state.errorState) {
      return (
        <div className="modal-row">
          { this.renderErrorState() }
        </div>
      );
    }
    return (
      <div className={`modal-row ${disabledClass}`}>
        <h4 className="row-title">{t('Select Section')}</h4>
        <Tooltip
          tooltipContent={t('Only sections with compatible asset types are shown below.')}
          tooltipId="select-section-info"
        >
          <span className="bff-tool-tip icon" aria-label="select section" />
        </Tooltip>
        <Select
          className="bulk-move-section-selector"
          closeOnSelect
          onChange={(option) => this.updateSelectedOption(option, 'destinationSectionKey')}
          options={this.selectOptionsFor('sections')}
          placeholder={t('Select a Section')}
          value={this.state.destinationSectionKey}
          clearable={false}
        />
      </div>
    );
  }

  renderBrandfolderSelection = () => {
    const { libraryName } = this.props;

    return (
      <div className="modal-row">
        <h4 className="row-title">{`${t('Select')} ${t(libraryName)}`}</h4>
        <Select
          options={this.selectOptionsFor('brandfolders')}
          onChange={(option) => this.updateSelectedOption(option, 'destinationBrandfolderKey')}
          placeholder={`${t('Select a')} ${t(libraryName)}`}
          value={this.state.destinationBrandfolderKey}
          closeOnSelect
          className="bulk-move-brandfolder-selector"
          clearable={false}
        />
      </div>
    );
  }

  changeDestinationTypeSelection(destination) {
    if (destination === 'sameBrandfolder') {
      this.setState({
        sameBrandfolder: true,
        disableSubmitButton: true,
        destinationSectionKey: null,
        destinationBrandfolderKey: Brandfolder.key,
        errorState: false
      }, () => {
        this.getTransferrableSections();
      });
    } else if (destination === 'differentBrandfolder') {
      this.setState({
        sameBrandfolder: false,
        disableSubmitButton: true,
        destinationSectionKey: null,
        destinationBrandfolderKey: null,
        errorState: false,
        sectionOptions: []
      });
    }
  }

  renderDestinationTypeSelection() {
    const { libraryName } = this.props;

    if (!this.state.brandfolderOptions.length) { return null; }

    return (
      <div className="radio-button-container">
        <label className="radio-row">
          <h4>{t('To another Section')}</h4>
          <input
            checked={this.state.sameBrandfolder}
            className="radio-input"
            name="radio"
            onChange={() => this.changeDestinationTypeSelection('sameBrandfolder')}
            type="radio"
            value="another-section-in-this-brandfolder"
          />
          <span className="radio-dot" />
        </label>
        <label className="radio-row">
          <h4>{`${t('To another')} ${t(libraryName)}`}</h4>
          <input
            checked={!this.state.sameBrandfolder}
            className="radio-input"
            name="radio"
            onChange={() => this.changeDestinationTypeSelection('differentBrandfolder')}
            type="radio"
            value="another-brandfolder"
          />
          <span className="radio-dot" />
        </label>
      </div>
    );
  }

  renderMoveContent() {
    if (this.state.sameBrandfolder) {
      return (
        <div className="modal-row">
          {this.renderSectionSelection()}
        </div>
      );
    }

    return (
      <div className="modal-row">
        {this.renderBrandfolderSelection()}
        {this.renderSectionSelection()}
      </div>
    );
  }

  renderErrorState() {
    const { errorState } = this.state;
    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const thisThese = assetQtyObject.plural ? t('these') : t('this');
    const assetCopy = assetQtyObject.plural ? t('assets') : t('asset');

    if (!errorState) {
      return null;
    }

    if (errorState === 'multipleTypes') {
      return (
        <React.Fragment>
          <p className="move-error-copy">{t('Unable to move assets.')}</p>
          <p className="move-error-copy">
            {`${t('Selected')} ${assetCopy} ${t('must be from the same type of section in order to move them.')}`}
          </p>
        </React.Fragment>
      );
    }

    if (errorState === "noSectionOptions") {
      return (
        <React.Fragment>
          <p className="move-error-copy">
            {`${t('Unable to move')} ${thisThese} ${assetCopy} ${t('within this Brandfolder.')}`}
          </p>
          <p className="move-error-copy">
            {t('There are no eligible sections to receive these assets.')}
          </p>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <p className="move-error-copy">{t('An error occured.')}</p>
        <p className="move-error-copy">
          {t('Please try again and contact support if the issue persists.')}
        </p>
      </React.Fragment>
    );
  }

  render() {
    const { isLoading } = this.state;
    const loaderActive = isLoading ? 'loader-active' : '';
    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const assetCopy = assetQtyObject.plural ? t('assets') : t('asset');

    return (
      <div className="modal-content-wrapper bulk-move-modal">
        <div className="modal-content-wrapper__header">
          <span className="bff-move icon" aria-hidden="true" />
          <h3 className="modal-title">{t('Move')}
            <span className="bold-text">
              {this.props.selectedAssetKeys.size} {assetCopy}
            </span>
          </h3>
          <button
            className="close-button"
            onClick={this.props.closeModal}
            type="button"
          >
            <span className="bff-close" />
          </button>
        </div>
        <div className={`modal-content-wrapper__body ${loaderActive}`}>
          {isLoading
            ? <BFLoader />
            : (
              <div>
                <div className="modal-row">
                  { this.renderDestinationTypeSelection() }
                </div>
                { this.renderMoveContent() }
                <div className="button-container">
                  <button
                    className="button sm primary t-confirm-modal"
                    disabled={this.state.disableSubmitButton}
                    onClick={() => this.moveAssets()}
                    type="button"
                  >
                    {t('Move')}
                  </button>
                </div>
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

BulkMoveModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  libraryName: PropTypes.string,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
  selectedAssetTypes: PropTypes.shape({}).isRequired,
};

BulkMoveModal.defaultProps = {
  libraryName: "Brandfolder"
};

const ModalComponent = renderModal(BulkMoveModal, 'BulkMoveModal');
export default ModalComponent;
